import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/useAuth';
import { formatKpiValue } from '@/features/insight/format';
import { useWorkingContext } from '@/hooks/useWorkingContext';
import { examinationsApi } from '@/services/api/endpoints/examinations';
import { insightApi } from '@/services/api/endpoints/insight';
import { notificationsApi } from '@/services/api/endpoints/notifications';
import { queryKeys } from '@/services/api/queryKeys';
import type { DashboardSummary, Kpi } from '@/types/insight';
import { PERMISSIONS } from '@/types/permissions';

/*
 * Small real figures for the launcher and module cards.
 *
 * Three sources, all existing endpoints and all permission-gated exactly as the
 * server gates them: the dashboard summary (one request; each section is only
 * returned when the caller may see it), the unread-notification count (the same
 * query the header's bell already polls, so it is shared, not repeated), and
 * the number of scheduled examinations. A figure that is not available is
 * absent — never zero, never a placeholder.
 */

export interface ModuleBadges {
  /** By module key. */
  modules: Record<string, string>;
  /** By module key, then sub-module key. */
  subModules: Record<string, Record<string, string>>;
  /** True while any source is still loading for the first time. */
  loading: boolean;
  /** True when a source failed; the cards still work, they just show fewer figures. */
  failed: boolean;
  retry: () => void;
}

function plural(count: number, one: string, many: string): string {
  return `${count.toLocaleString()} ${count === 1 ? one : many}`;
}

function kpi(summary: DashboardSummary | undefined, section: string, key: string): Kpi | undefined {
  const found = summary?.sections.find((candidate) => candidate.key === section && !candidate.unavailable);
  return found?.kpis.find((candidate) => candidate.key === key);
}

export function useModuleBadges(): ModuleBadges {
  const { can } = useAuth();
  const { campusId } = useWorkingContext();

  const summary = useQuery({
    queryKey: queryKeys.insight.summary(campusId),
    queryFn: () => insightApi.summary(campusId ?? undefined),
    enabled: can(PERMISSIONS.DASHBOARD_VIEW),
    staleTime: 60_000,
  });

  const unread = useQuery({
    queryKey: queryKeys.notifications.unreadCount,
    queryFn: notificationsApi.unreadCount,
    enabled: can(PERMISSIONS.NOTIFICATION_SELF_VIEW),
    staleTime: 30_000,
  });

  const scheduledExamsParams = { page: 0, size: 1, status: 'SCHEDULED' as const };
  const scheduledExams = useQuery({
    queryKey: queryKeys.examinations.list(scheduledExamsParams),
    queryFn: () => examinationsApi.list(scheduledExamsParams),
    enabled: can(PERMISSIONS.EXAMINATION_VIEW),
    staleTime: 60_000,
  });

  const modules: Record<string, string> = {};
  const subModules: Record<string, Record<string, string>> = {};
  const setSub = (module: string, sub: string, text: string) => {
    subModules[module] = { ...subModules[module], [sub]: text };
  };

  const data = summary.data;
  const activeStudents = kpi(data, 'students', 'activeStudents');
  if (activeStudents !== undefined) {
    modules.students = plural(activeStudents.value, 'active student', 'active students');
    setSub('students', 'profiles', plural(activeStudents.value, 'active student', 'active students'));
  }
  const awaiting = kpi(data, 'admissions', 'awaitingDecision');
  if (awaiting !== undefined) {
    setSub('students', 'admissions', plural(awaiting.value, 'application in progress', 'applications in progress'));
  }
  const outstanding = kpi(data, 'finance', 'outstanding');
  if (outstanding !== undefined) {
    modules.finance = `${formatKpiValue(outstanding)} outstanding`;
    setSub('finance', 'invoices', `${formatKpiValue(outstanding)} outstanding`);
  }
  const collected = kpi(data, 'finance', 'collectedThisMonth');
  if (collected !== undefined) {
    setSub('finance', 'payments', `${formatKpiValue(collected)} collected this month`);
  }
  const headcount = kpi(data, 'staff', 'headcount');
  if (headcount !== undefined) {
    modules.staff = plural(headcount.value, 'staff member', 'staff members');
  }
  const leaveAwaiting = kpi(data, 'staff', 'leaveAwaitingDecision');
  if (leaveAwaiting !== undefined) {
    modules['hr-payroll'] = plural(leaveAwaiting.value, 'leave request awaiting decision', 'leave requests awaiting decision');
    setSub('hr-payroll', 'leave', plural(leaveAwaiting.value, 'awaiting decision', 'awaiting decision'));
  }
  const overdue = kpi(data, 'library', 'overdueLoans');
  if (overdue !== undefined) {
    modules.library = plural(overdue.value, 'overdue loan', 'overdue loans');
    setSub('library', 'overdue', plural(overdue.value, 'overdue loan', 'overdue loans'));
  }
  const dueToday = kpi(data, 'library', 'dueToday');
  if (dueToday !== undefined) {
    setSub('library', 'loans', `${dueToday.value.toLocaleString()} due today`);
  }
  const tripsToday = kpi(data, 'transport', 'tripsToday');
  if (tripsToday !== undefined) {
    modules.transport = plural(tripsToday.value, 'trip today', 'trips today');
    setSub('transport', 'operations', plural(tripsToday.value, 'trip today', 'trips today'));
  }
  const incidents = kpi(data, 'transport', 'openIncidents');
  if (incidents !== undefined) {
    setSub('transport', 'incidents', plural(incidents.value, 'open incident', 'open incidents'));
  }
  const lowStock = kpi(data, 'assets', 'lowStock');
  if (lowStock !== undefined) {
    modules.assets = plural(lowStock.value, 'stock item at reorder level', 'stock items at reorder level');
    setSub('assets', 'stock', plural(lowStock.value, 'item at reorder level', 'items at reorder level'));
  }

  const examCount = scheduledExams.data?.pagination.totalElements;
  if (examCount !== undefined) {
    modules.examinations = plural(examCount, 'scheduled exam', 'scheduled exams');
    setSub('examinations', 'exams', plural(examCount, 'scheduled exam', 'scheduled exams'));
  }

  const unreadCount = unread.data?.unread;
  if (unreadCount !== undefined) {
    modules.workspace = plural(unreadCount, 'unread notification', 'unread notifications');
    setSub('workspace', 'notifications', `${unreadCount.toLocaleString()} unread`);
  }

  return {
    modules,
    subModules,
    loading: summary.isLoading || unread.isLoading || scheduledExams.isLoading,
    failed: summary.isError || unread.isError || scheduledExams.isError,
    retry: () => {
      void summary.refetch();
      void unread.refetch();
      void scheduledExams.refetch();
    },
  };
}
